import { useEffect, useRef } from "react";

import { fetchFreelancerProfile } from "@js/apps/freelancer/actions";
import { useFreelancerProfile } from "@js/apps/freelancer/hooks";
import { useAppDispatch } from "@js/hooks";
import { useLatestCallback } from "@js/hooks/use-latest-callback";
import type { Freelancer } from "@js/types/freelancer";

export type UseWaitForIdentityVerificationResultConfig = {
  onVerificationSuccess?: () => void;
  onVerificationFail?: () => void;
};

const POLL_INTERVAL_MS = 3000;

export const useWaitForIdentityVerificationResult = () => {
  const dispatch = useAppDispatch();
  const freelancerProfile = useFreelancerProfile();
  const timeoutRef = useRef<number>(null);

  const waitForIdentityVerificationResult = useLatestCallback(
    ({
      onVerificationSuccess,
      onVerificationFail,
    }: UseWaitForIdentityVerificationResultConfig) => {
      if (!freelancerProfile?.id) return;
      const handleVerificationStatus = (status: Freelancer["idv_status"]) => {
        if (status === ENUMS.IdentityVerificationStatuses.COMPLETED) {
          onVerificationSuccess?.();
          return true;
        } else if (status === ENUMS.IdentityVerificationStatuses.FAILED) {
          onVerificationFail?.();
          return true;
        }

        return false;
      };
      const hasResult = handleVerificationStatus(freelancerProfile.idv_status);
      if (hasResult) {
        return;
      }

      const pollProfileAndCheckResult = async () => {
        const profile = await dispatch(
          fetchFreelancerProfile(freelancerProfile.id),
        );

        const status = profile?.idv_status;

        const hasIDVResult = handleVerificationStatus(status ?? null);
        if (hasIDVResult) {
          return;
        }

        timeoutRef.current = window.setTimeout(
          pollProfileAndCheckResult,
          POLL_INTERVAL_MS,
        );
      };

      void pollProfileAndCheckResult();
    },
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return { waitForIdentityVerificationResult };
};
